import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';

import { LocationSelectionService } from '../../service/location-selection.service';
import { T3AnalysisStore } from '../../../core/shared/t3-model/t3-analysis.store';
import {
    T3SimulationParameters,
    T3SimulationService
} from '../../../core/shared/t3-model/t3-simulation.service';
import { T3ApiResponse } from '../../../core/shared/t3-model/t3-response-model';

type Metric = {
    label: string;
    value: number;
    icon?: string;
};

type PanelData = {
    coverage: number;
    coverageTarget: number;
    startYear?: number;
    yearsToTarget: number;
    metrics: Metric[];
};

type Panel = {
    id: string;
    title: string;
    interventionName: string;
    icon: string;
    enabled: boolean;
    data: PanelData;
};

type UiSim = {
    name: string;
    createdAt: number;
    params: T3SimulationParameters;
};

@Component({
    selector: 'app-t3-simulation',
    standalone: true,
    imports: [CommonModule, FormsModule, CheckboxModule, DialogModule, InputTextModule],
    templateUrl: './t3-simulation-panel.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class T3SimulationComponent implements OnDestroy {
    private store = inject(T3AnalysisStore);
    private simApi = inject(T3SimulationService);
    private locationSel = inject(LocationSelectionService);
    private cdr = inject(ChangeDetectorRef);
    private destroy$ = new Subject<void>();

    expandedPanelId: string | null = 'healthier-sg';

    busy = false;
    errorMsg = '';

    runDialogVisible = false;
    renameDialogVisible = false;
    viewDialogVisible = false;
    deleteDialogVisible = false;

    runName = '';
    renameOld = '';
    renameNew = '';
    viewSim: UiSim | null = null;
    deletePendingName = '';

    sims: UiSim[] = [];

    panels: Panel[] = [
        {
            id: 'healthier-sg',
            title: 'Healthier SG',
            interventionName: 'Healthier SG',
            icon: 'pi pi-heart',
            enabled: true,
            data: {
                coverage: 0,
                coverageTarget: 90,
                yearsToTarget: 1.5,
                metrics: [
                    { label: 'increase diagnosis rate', value: 15, icon: 'pi pi-search' },
                    { label: 'increase treatment uptake rate', value: 0, icon: 'pi pi-cog' },
                    { label: 'decrease dropout rate', value: 10, icon: 'pi pi-chart-line' },
                    { label: 'increase control rate', value: 0, icon: 'pi pi-clipboard' }
                ]
            }
        },
        {
            id: 'ptec',
            title: 'PTEC',
            interventionName: 'PTEC',
            icon: 'pi pi-building',
            enabled: false,
            data: {
                coverage: 0,
                coverageTarget: 20,
                startYear: 2023,
                yearsToTarget: 5,
                metrics: [
                    { label: 'increase on control rate', value: 16, icon: 'pi pi-clipboard' },
                    { label: 'decrease on dropout rate', value: 0, icon: 'pi pi-chart-line' }
                ]
            }
        },
        {
            id: 'enhanced-treatment',
            title: 'Enhanced Treatment',
            interventionName: 'Enhanced Treatment',
            icon: 'pi pi-tablet',
            enabled: false,
            data: {
                coverage: 0,
                coverageTarget: 27,
                startYear: 2027,
                yearsToTarget: 1,
                metrics: [
                    { label: 'increase on control rate', value: 8.5, icon: 'pi pi-clipboard' },
                    { label: 'increase on treatment uptake', value: 10, icon: 'pi pi-cog' }
                ]
            }
        },
        {
            id: 'enhanced-diagnosis',
            title: 'Enhanced Diagnosis',
            interventionName: 'Enhanced Diagnosis',
            icon: 'pi pi-search',
            enabled: false,
            data: {
                coverage: 0,
                coverageTarget: 100,
                startYear: 2027,
                yearsToTarget: 1,
                metrics: [
                    { label: 'increase on diagnosis rate population 30 to 40', value: 20, icon: 'pi pi-search' },
                    { label: 'increase on diagnosis rate population 40 and above', value: 10, icon: 'pi pi-search' },
                    { label: 'increase on treatment uptake population 30 to 40', value: 10, icon: 'pi pi-cog' },
                    { label: 'increase on treatment uptake population 40 and above', value: 10, icon: 'pi pi-cog' }
                ]
            }
        }
    ];

    constructor() {
        this.syncSimsFromStore();

        this.store.runsList$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.syncSimsFromStore();
                this.cdr.markForCheck();
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    isExpanded(panel: Panel): boolean {
        return this.expandedPanelId === panel.id;
    }

    stopPropagation(event: Event): void {
        event.stopPropagation();
    }

    togglePanel(panel: Panel): void {
        this.expandedPanelId = this.expandedPanelId === panel.id ? null : panel.id;
    }

    openRunDialog(): void {
        this.errorMsg = '';
        this.runName = this.nextDefaultName();
        this.runDialogVisible = true;
        this.cdr.markForCheck();
    }

    async confirmRun(): Promise<void> {
        const name = this.normalizeName(this.runName);

        if (!name) {
            return;
        }

        if (this.hasName(name)) {
            this.errorMsg = 'Simulation name already exists.';
            this.cdr.markForCheck();
            return;
        }

        const parameterDetails = this.buildParameterDetails();
        const interventionNames = this.buildInterventionNames();

        if (this.hasDuplicatePayload(parameterDetails, interventionNames)) {
            this.errorMsg = 'A simulation with the same intervention configuration already exists.';
            this.cdr.markForCheck();
            return;
        }

        await this.runSimulation(name, parameterDetails, interventionNames);
    }

    private async runSimulation(
        name: string,
        parameterDetails: T3SimulationParameters,
        interventionNames: string
    ): Promise<void> {
        this.errorMsg = '';
        this.busy = true;
        this.runDialogVisible = false;
        this.cdr.markForCheck();

        try {
            const session_id = this.ensureSessionId();
            const city = this.locationSel.getSelected()?.city || '';
            const userId = this.getUserId();

            if (!session_id) {
                throw new Error('Missing session_id in sessionStorage.');
            }

            if (!city) {
                throw new Error('Missing selected city.');
            }

            if (!userId) {
                throw new Error('Missing user id.');
            }

            const response = await this.simApi.createSimulation({
                session_id,
                id: userId,
                city_name: city,
                simulation_run_name: name,
                intervention_names: interventionNames,
                parameter_details: parameterDetails
            });

            const t3 = this.extractT3Response(response);

            if (!t3 || !Array.isArray(t3.years)) {
                console.error('[T3 Simulation] Unexpected response shape:', response);
                throw new Error((response as any)?.message || 'T3 simulation request failed.');
            }

            const displayName = this.normalizeName(name);

            (t3 as any).__ui_params = parameterDetails;
            (t3 as any).__intervention_names = interventionNames;

            this.store.upsertRun(t3, displayName, {
                intervention_names: interventionNames,
                parameter_details: parameterDetails
            });

            this.syncSimsFromStore();
        } catch (e: any) {
            this.errorMsg = e?.message || 'Something went wrong.';
        } finally {
            this.busy = false;
            this.cdr.markForCheck();
        }
    }

    openView(sim: UiSim): void {
        this.viewSim = sim;
        this.viewDialogVisible = true;
        this.cdr.markForCheck();
    }

    openRename(sim: UiSim): void {
        this.errorMsg = '';
        this.renameOld = sim.name;
        this.renameNew = sim.name;
        this.renameDialogVisible = true;
        this.cdr.markForCheck();
    }

    async confirmRename(): Promise<void> {
        const oldName = this.normalizeName(this.renameOld);
        const newName = this.normalizeName(this.renameNew);

        if (!oldName || !newName) {
            return;
        }

        if (this.isBaselineName(oldName)) {
            this.errorMsg = 'Baseline cannot be renamed.';
            this.cdr.markForCheck();
            return;
        }

        if (oldName.toLowerCase() !== newName.toLowerCase() && this.hasName(newName)) {
            this.errorMsg = 'Simulation name already exists.';
            this.cdr.markForCheck();
            return;
        }

        this.busy = true;
        this.cdr.markForCheck();

        try {
            const session_id = this.ensureSessionId();
            const city = this.locationSel.getSelected()?.city || '';
            const userId = this.getUserId();

            if (!city) {
                throw new Error('Missing selected city.');
            }

            if (!userId) {
                throw new Error('Missing user id.');
            }

            const response = await this.simApi.renameSimulation({
                session_id,
                city,
                id: userId,
                old_simulation_run_name: oldName,
                new_simulation_run_name: newName
            });

            if (!this.isOkStatus(response, ['success', 'updated'])) {
                throw new Error(response?.message || 'Rename failed.');
            }

            this.store.renameRunTitle(oldName, newName);
            this.renameDialogVisible = false;
            this.syncSimsFromStore();
        } catch (e: any) {
            this.errorMsg = e?.message || 'Something went wrong.';
        } finally {
            this.busy = false;
            this.cdr.markForCheck();
        }
    }

    requestDelete(sim: UiSim): void {
        if (this.isBaselineName(sim.name)) {
            this.errorMsg = 'Baseline cannot be deleted.';
            this.cdr.markForCheck();
            return;
        }

        this.deletePendingName = sim.name;
        this.deleteDialogVisible = true;
        this.cdr.markForCheck();
    }

    async confirmDelete(): Promise<void> {
        const name = this.normalizeName(this.deletePendingName);

        this.deleteDialogVisible = false;
        this.deletePendingName = '';

        if (!name) {
            return;
        }

        this.busy = true;
        this.cdr.markForCheck();

        try {
            const session_id = this.ensureSessionId();
            const city = this.locationSel.getSelected()?.city || '';
            const userId = this.getUserId();

            if (!city) {
                throw new Error('Missing selected city.');
            }

            if (!userId) {
                throw new Error('Missing user id.');
            }

            const response = await this.simApi.deleteSimulation({
                session_id,
                city,
                id: userId,
                old_simulation_run_name: name
            });

            if (!this.isOkStatus(response, ['success', 'deleted'])) {
                throw new Error(response?.message || 'Delete failed.');
            }

            this.store.deleteRunsByTitle([name]);
            this.syncSimsFromStore();
        } catch (e: any) {
            this.errorMsg = e?.message || 'Something went wrong.';
        } finally {
            this.busy = false;
            this.cdr.markForCheck();
        }
    }

    cancelDelete(): void {
        this.deleteDialogVisible = false;
        this.deletePendingName = '';
        this.cdr.markForCheck();
    }

    isBaselineUi(sim: UiSim): boolean {
        return this.isBaselineName(sim.name);
    }

    private buildInterventionNames(): string {
        return this.panels
            .filter((panel) => panel.enabled)
            .map((panel) => panel.interventionName)
            .join(', ');
    }

    private hasDuplicatePayload(
        parameterDetails: T3SimulationParameters,
        interventionNames: string
    ): boolean {
        const currentSignature = this.simulationPayloadSignature(parameterDetails, interventionNames);

        return this.store.getRunsSnapshot().some((run) => {
            if (this.isBaselineName(run.title)) {
                return false;
            }

            const storedParams =
                (run.params as any)?.parameter_details ??
                (run.t3 as any)?.__ui_params ??
                (run.t3 as any)?.parameter_details ??
                run.params ??
                {};

            const storedInterventions =
                (run.params as any)?.intervention_names ??
                (run.t3 as any)?.__intervention_names ??
                (run.t3 as any)?.intervention_names ??
                '';

            const existingSignature = this.simulationPayloadSignature(storedParams, storedInterventions);

            return existingSignature === currentSignature;
        });
    }

    private simulationPayloadSignature(
        parameterDetails: any,
        interventionNames: string
    ): string {
        return this.stableStringify({
            intervention_names: this.normalizeInterventionNames(interventionNames),
            parameter_details: parameterDetails || {}
        });
    }

    private normalizeInterventionNames(value: string): string {
        return String(value || '')
            .split(',')
            .map((x) => x.trim())
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b))
            .join(', ');
    }

    private stableStringify(value: any): string {
        if (value === null || value === undefined) {
            return JSON.stringify(value);
        }

        if (Array.isArray(value)) {
            return `[${value.map((item) => this.stableStringify(item)).join(',')}]`;
        }

        if (typeof value === 'object') {
            return `{${Object.keys(value)
                .sort()
                .map((key) => `${JSON.stringify(key)}:${this.stableStringify(value[key])}`)
                .join(',')}}`;
        }

        if (typeof value === 'number') {
            return JSON.stringify(Number.isFinite(value) ? Number(value) : 0);
        }

        return JSON.stringify(value);
    }

    private buildParameterDetails(): T3SimulationParameters {
        const hsg = this.panelById('healthier-sg');
        const ptec = this.panelById('ptec');
        const enhancedTreatment = this.panelById('enhanced-treatment');
        const enhancedDiagnosis = this.panelById('enhanced-diagnosis');

        return {
            'switch HSG': this.switchValue(hsg),
            'HSG coverage': this.numberValue(hsg?.data.coverage),
            'goal HSG % coverage': this.numberValue(hsg?.data.coverageTarget),
            'time to adjust HSG coverage': this.numberValue(hsg?.data.yearsToTarget),
            'effect % HSG on CoC[diagnosis]': this.metricValue(hsg, 0),
            'effect % HSG on CoC[hypertension treatment uptake]': this.metricValue(hsg, 1),
            'effect % HSG on CoC[hypertension treatment cease]': this.metricValue(hsg, 2),
            'effect % HSG on CoC[hypertension control]': this.metricValue(hsg, 3),

            'switch PTEC': this.switchValue(ptec),
            'PTEC coverage': this.numberValue(ptec?.data.coverage),
            'goal PTEC % coverage': this.numberValue(ptec?.data.coverageTarget),
            'start time PTEC': this.numberValue(ptec?.data.startYear),
            'time to adjust PTEC coverage': this.numberValue(ptec?.data.yearsToTarget),
            'effect % PTEC on CoC[hypertension control]': this.metricValue(ptec, 0),
            'effect % PTEC on CoC[hypertension treatment cease]': this.metricValue(ptec, 1),

            'switch NUHS Enhanced Treatments': this.switchValue(enhancedTreatment),
            'NUHS Enhanced Treatments coverage': this.numberValue(enhancedTreatment?.data.coverage),
            'goal NUHS Enhanced Treatments % coverage': this.numberValue(enhancedTreatment?.data.coverageTarget),
            'start time NUHS Enhanced Treatments': this.numberValue(enhancedTreatment?.data.startYear),
            'time to adjust NUHS Enhanced Treatments coverage': this.numberValue(enhancedTreatment?.data.yearsToTarget),
            'effect % NUHS Enhanced Treatments on CoC[hypertension control]': this.metricValue(enhancedTreatment, 0),
            'effect % NUHS Enhanced Treatments on CoC[hypertension treatment uptake]': this.metricValue(enhancedTreatment, 1),

            'switch NUHS Enhanced Diagnosis': this.switchValue(enhancedDiagnosis),
            'Enhanced Diagnosis coverage': this.numberValue(enhancedDiagnosis?.data.coverage),
            'goal NUHS Enhanced Diagnosis % coverage': this.numberValue(enhancedDiagnosis?.data.coverageTarget),
            'start time NUHS Enhanced Diagnosis': this.numberValue(enhancedDiagnosis?.data.startYear),
            'time to adjust NUHS Enhanced Diagnosis coverage': this.numberValue(enhancedDiagnosis?.data.yearsToTarget),
            'effect % NUHS Enhanced Diagnosis on CoC in population 30 to 40[diagnosis]': this.metricValue(enhancedDiagnosis, 0),
            'effect % NUHS Enhanced Diagnosis on CoC in population 40 plus[diagnosis]': this.metricValue(enhancedDiagnosis, 1),
            'effect % NUHS Enhanced Diagnosis on CoC in population 30 to 40[hypertension treatment uptake]': this.metricValue(enhancedDiagnosis, 2),
            'effect % NUHS Enhanced Diagnosis on CoC in population 40 plus[hypertension treatment uptake]': this.metricValue(enhancedDiagnosis, 3)
        };
    }

    private panelById(id: string): Panel | undefined {
        return this.panels.find((panel) => panel.id === id);
    }

    private switchValue(panel: Panel | undefined): number {
        return panel?.enabled ? 1 : 0;
    }

    private metricValue(panel: Panel | undefined, index: number): number {
        return this.numberValue(panel?.data.metrics?.[index]?.value);
    }

    private numberValue(value: any): number {
        const n = Number(value);
        return Number.isFinite(n) ? n : 0;
    }

    private extractT3Response(response: any): T3ApiResponse | null {
        if (!response) {
            return null;
        }

        const payload =
            response?.htn_output ??
            response?.data?.htn_output ??
            response?.response?.htn_output ??
            response?.data ??
            response?.response ??
            response;

        if (!payload || !Array.isArray(payload?.years)) {
            return null;
        }

        return {
            ...payload,
            status: response?.status ?? payload?.status ?? 'success',
            city_name: response?.city_name ?? payload?.city_name,
            id: response?.id,
            session: response?.session,
            intervention_names: response?.intervention_names,
            parameter_details: response?.parameter_details
        } as T3ApiResponse;
    }

    private syncSimsFromStore(): void {
        const runs = this.store.getRunsSnapshot();

        this.sims = runs.map((run) => ({
            name: run.title,
            createdAt: run.createdAt,
            params:
                run.params ||
                {
                    intervention_names: (run.t3 as any)?.__intervention_names || (run.t3 as any)?.intervention_names || '',
                    parameter_details: (run.t3 as any)?.__ui_params || (run.t3 as any)?.parameter_details || {}
                }
        }));
    }

    private hasName(name: string): boolean {
        const normalized = this.normalizeName(name).toLowerCase();
        return this.sims.some((sim) => this.normalizeName(sim.name).toLowerCase() === normalized);
    }

    private nextDefaultName(): string {
        const nums = new Set<number>();

        for (const sim of this.sims) {
            const match = /^simulation\s+(\d+)$/i.exec(this.normalizeName(sim.name));
            if (match) nums.add(Number(match[1]));
        }

        let index = 1;
        while (nums.has(index)) index++;

        return `Simulation ${index}`;
    }

    private normalizeName(value: string): string {
        return String(value || '').trim();
    }

    private isBaselineName(name: string): boolean {
        const normalized = this.normalizeName(name).toLowerCase();
        return normalized === 'baseline' || normalized === 'basic simulation';
    }

    private getUserId(): string {
        return (
            sessionStorage.getItem('userEmail') ||
            sessionStorage.getItem('userEmailFromMe') ||
            ''
        ).trim().toLowerCase();
    }

    private ensureSessionId(): string {
        const key = 'session_id';

        let id =
            sessionStorage.getItem(key) ||
            localStorage.getItem(key) ||
            '';

        if (!id) {
            id = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        }

        try {
            sessionStorage.setItem(key, id);
            localStorage.setItem(key, id);
        } catch { }

        return id;
    }

    private isOkStatus(response: any, allowed: string[]): boolean {
        const status = String(response?.status || '').trim().toLowerCase();
        return allowed.map((x) => x.toLowerCase()).includes(status);
    }
}